import { SESSION_KEY } from '#pplx/frontend/apps/ask/auth/getSession.js';
import { USER_SETTINGS_PREFIX } from '#pplx/frontend/apps/ask/user-settings/storage/constants.js';
import { migrateKeysToAccount } from '#pplx/frontend/browser-storage/accountStorage.js';
import { getActiveAccountId, setActiveAccountId, } from '#pplx/frontend/browser-storage/activeAccount.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { migrateEppoCacheToAccount } from '#pplx/frontend/experimentation/IndexedDBStore.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { migrateQueryCacheToAccount } from '#pplx/frontend/react-query/queryCacheStore.js';
/**
 * Pre-activation global localStorage keys to copy into the primary account's namespace on first
 * activation. Deliberately only **user-authored / identity state**: the session and per-user
 * settings. Caches (history, weather, widgets, …) are omitted — they
 * repopulate on their own, and migrating them would only bloat the namespace.
 */
const PRIMARY_MIGRATION_KEYS = [SESSION_KEY];
const PRIMARY_MIGRATION_PREFIXES = [USER_SETTINGS_PREFIX];
/**
 * Bootstrap activation for the primary account (multiaccount web, plan "Activation B").
 *
 * Binds this tab's active account to the signed-in primary's user id (so `X-Pplx-Account` is sent
 * and storage is namespaced) and runs the one-time migration of the primary's existing global
 * localStorage into its namespace, so nothing appears to reset on first activation.
 *
 * Call after `resolveBootstrapConfig()` (it needs the session) and only when `setAccountForTab()`
 * returned no account — i.e. no more-specific source (`?pplx_account=` deep-link, existing
 * per-tab sessionStorage, last-visible seed) bound the tab. That guarantee is what keeps the
 * migration leak-safe: with no active account the session resolves against the primary cookie,
 * so it only ever copies the *primary's* data into the *primary's* namespace (never a
 * secondary's).
 */
export function activatePrimaryAccount({ cachedSession, sessionFetchPromise, }) {
    if (cachedSession) {
        // Synchronous path: bind before the app mounts so the active account is set for the first
        // namespaced reads. Catch here too — `bindPrimary` throws on a bad session, and unlike the
        // async path below there's no promise to route it through.
        try {
            bindPrimary(cachedSession);
        }
        catch (error) {
            warnBindFailed(error);
        }
        return;
    }
    void sessionFetchPromise.then(bindPrimary).catch(warnBindFailed);
}
function warnBindFailed(error) {
    logger.warn('multiaccount activation: failed to bind primary account', error);
}
function bindPrimary(session) {
    const userId = session?.user?.id;
    if (!validateUuid(userId)) {
        // The session resolved against the primary cookie but carried no valid user id — there's no
        // primary to activate. Throw so the caller's logger.warn surfaces it (an authenticated
        // bootstrap that reaches activation shouldn't lack a primary id).
        throw new Error('multiaccount activation: session resolved without a valid primary user id');
    }
    // Re-check after the (possibly async) session resolve: if something bound the tab in the
    // meantime, leave it and skip migration (it's no longer guaranteed to be the primary).
    if (getActiveAccountId()) {
        return;
    }
    // No active account was set, so the session fetch carried no `X-Pplx-Account` and resolved
    // against the primary cookie — `userId` is the primary. Seed its namespace from the
    // pre-activation global keys *before* binding, so the first namespaced reads find the data.
    migrateKeysToAccount({
        accountId: userId,
        keys: PRIMARY_MIGRATION_KEYS,
        prefixes: PRIMARY_MIGRATION_PREFIXES,
    });
    // Seed the account-scoped IndexedDB caches from their legacy stores. These are fire-and-forget;
    // the Eppo accessor waits for its registered migration before the first account-scoped read.
    void migrateQueryCacheToAccount(userId).catch((error) => {
        logger.warn('multiaccount activation: failed to migrate query cache to account', error);
    });
    void migrateEppoCacheToAccount(userId).catch((error) => {
        logger.warn('multiaccount activation: failed to migrate Eppo cache to account', error);
    });
    setActiveAccountId(userId);
}
//# sourceMappingURL=activateMultiaccount.js.map